<script setup>
import { ref, computed } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import { RefreshCw, Search, CheckCircle2, AlertTriangle, Check, ArrowRight, X, Info } from '@lucide/vue';

const props = defineProps({
  category: { type: Object, required: true },
  operators: { type: Array, default: () => [] },
  ppobConfig: { type: Object, default: () => ({}) },
  formatRupiah: { type: Function, required: true },
});

// Isolated Pulsa State
const phoneNumber = ref('');
const isDetecting = ref(false);
const detectedOperator = ref(null);
const detectionError = ref('');
const manualOverride = ref(false);
const selectedOperatorId = ref(null);

const inquiryResult = ref(null);
const inquiryError = ref('');
const isChecking = ref(false);

const selectedSku = ref(null);
const reviewModalOpen = ref(false);
const isSubmitting = ref(false);
const purchaseResult = ref(null);
const resultModalOpen = ref(false);
const idempotencyKey = ref('');

let detectionTimeout = null;

function normalizePhoneInput(raw) {
  let cleaned = raw.replace(/[\s-]/g, '').replace(/\+/g, '').replace(/\D/g, '');
  if (cleaned.startsWith('62')) {
    cleaned = '0' + cleaned.substring(2);
  }
  return cleaned;
}

function onPhoneInput(e) {
  const cleaned = normalizePhoneInput(e.target.value);
  e.target.value = cleaned;
  phoneNumber.value = cleaned;

  // Clear downstream state
  inquiryResult.value = null;
  selectedSku.value = null;
  inquiryError.value = '';
  purchaseResult.value = null;
  reviewModalOpen.value = false;
  resultModalOpen.value = false;

  if (cleaned.length < 4) {
    detectedOperator.value = null;
    selectedOperatorId.value = null;
    detectionError.value = '';
    return;
  }

  if (manualOverride.value) return;

  clearTimeout(detectionTimeout);
  isDetecting.value = true;
  detectionError.value = '';

  detectionTimeout = setTimeout(async () => {
    try {
      const res = await axios.post(route('catalogue.detect-mobile-operator'), {
        phone_number: phoneNumber.value,
      });

      if (res.data && res.data.detected && res.data.operator) {
        detectedOperator.value = res.data.operator;
        selectedOperatorId.value = res.data.operator.id;
        detectionError.value = '';

        if (phoneNumber.value.length >= 10 && phoneNumber.value.length <= 15) {
          runInquiry();
        }
      } else {
        detectedOperator.value = null;
        selectedOperatorId.value = null;
        detectionError.value = 'Operator belum dikenali. Periksa kembali nomor tujuan.';
      }
    } catch (err) {
      detectionError.value = 'Operator belum dikenali. Periksa kembali nomor tujuan.';
    } finally {
      isDetecting.value = false;
    }
  }, 300);
}

function selectOperatorManually(op) {
  selectedOperatorId.value = op.id;
  detectedOperator.value = op;
  manualOverride.value = true;
  detectionError.value = '';
  inquiryResult.value = null;
  selectedSku.value = null;

  if (phoneNumber.value.length >= 10 && phoneNumber.value.length <= 15) {
    runInquiry();
  }
}

const isValidPhoneNumber = computed(() => {
  return phoneNumber.value.length >= 10 && phoneNumber.value.length <= 15;
});

const matchingProduct = computed(() => {
  if (!selectedOperatorId.value) return null;
  return props.category.products.find(p => p.mobile_operator_id === selectedOperatorId.value || p.operator?.id === selectedOperatorId.value);
});

const availableSkus = computed(() => {
  if (!matchingProduct.value) return [];
  return (matchingProduct.value.skus || []).filter(s => s.is_active);
});

const canSelectSku = computed(() => {
  return Boolean(
    inquiryResult.value &&
    (inquiryResult.value.success || inquiryResult.value.status === 'success') &&
    detectedOperator.value &&
    !isChecking.value
  );
});

async function runInquiry() {
  if (!isValidPhoneNumber.value || isChecking.value) return;

  isChecking.value = true;
  inquiryError.value = '';
  detectionError.value = '';
  inquiryResult.value = null;
  selectedSku.value = null;

  try {
    // 1. Detect operator first if not set
    if (!detectedOperator.value || !selectedOperatorId.value) {
      const detectRes = await axios.post(route('catalogue.detect-mobile-operator'), {
        phone_number: phoneNumber.value,
      });

      if (detectRes.data && detectRes.data.detected && detectRes.data.operator) {
        detectedOperator.value = detectRes.data.operator;
        selectedOperatorId.value = detectRes.data.operator.id;
      } else {
        detectedOperator.value = null;
        selectedOperatorId.value = null;
        detectionError.value = 'Operator belum dikenali. Periksa kembali nomor tujuan.';
        isChecking.value = false;
        return;
      }
    }

    if (!matchingProduct.value) {
      detectionError.value = 'Operator belum dikenali. Periksa kembali nomor tujuan.';
      isChecking.value = false;
      return;
    }

    // 2. Call backend mock inquiry endpoint
    const res = await axios.post(route('pulsa.inquiries.store'), {
      master_product_id: matchingProduct.value.id,
      customer_number: phoneNumber.value,
    });

    if (res.data && (res.data.success || res.data.status === 'success')) {
      inquiryResult.value = res.data;
    } else {
      inquiryResult.value = null;
      if (phoneNumber.value.endsWith('999')) {
        inquiryError.value = 'Layanan simulasi sedang mengalami timeout. Silakan coba kembali.';
      } else if (phoneNumber.value.endsWith('000')) {
        inquiryError.value = 'Nomor tujuan tidak ditemukan.';
      } else {
        inquiryError.value = res.data.message || res.data.error_message || 'Pengecekan nomor gagal.';
      }
    }
  } catch (err) {
    inquiryResult.value = null;
    if (phoneNumber.value.endsWith('999')) {
      inquiryError.value = 'Layanan simulasi sedang mengalami timeout. Silakan coba kembali.';
    } else if (phoneNumber.value.endsWith('000')) {
      inquiryError.value = 'Nomor tujuan tidak ditemukan.';
    } else {
      inquiryError.value = err.response?.data?.message || err.response?.data?.error_message || 'Nomor tujuan tidak ditemukan.';
    }
  } finally {
    isChecking.value = false;
  }
}

function openReviewModal() {
  if (!canSelectSku.value || !selectedSku.value) return;
  idempotencyKey.value = crypto.randomUUID();
  reviewModalOpen.value = true;
}

async function confirmPurchase() {
  if (!canSelectSku.value || !selectedSku.value || isSubmitting.value) return;

  isSubmitting.value = true;
  inquiryError.value = '';

  try {
    const res = await axios.post(route('pulsa.mock-transactions.store'), {
      inquiry_reference: inquiryResult.value.inquiry_reference,
      master_sku_id: selectedSku.value.id,
      idempotency_key: idempotencyKey.value,
    });

    if (res.data && (res.data.success || res.data.status === 'success')) {
      purchaseResult.value = res.data;
      reviewModalOpen.value = false;
      resultModalOpen.value = true;

      router.reload({ only: ['transactions'] });
    } else {
      inquiryError.value = res.data.message || res.data.error_message || 'Simulasi transaksi gagal.';
    }
  } catch (err) {
    inquiryError.value = err.response?.data?.message || 'Terjadi kesalahan saat simulasi transaksi.';
  } finally {
    isSubmitting.value = false;
  }
}

function resetFlow() {
  phoneNumber.value = '';
  detectedOperator.value = null;
  selectedOperatorId.value = null;
  manualOverride.value = false;
  inquiryResult.value = null;
  selectedSku.value = null;
  inquiryError.value = '';
  purchaseResult.value = null;
  reviewModalOpen.value = false;
  resultModalOpen.value = false;
}

function maskIdentifier(id) {
  if (!id) return '';
  if (id.length <= 6) return id;
  return id.substring(0, 4) + '****' + id.substring(id.length - 4);
}
</script>

<template>
  <div class="space-y-6">
    <!-- 1. Destination Phone Input & Operator Detection Row -->
    <div class="p-6 rounded-3xl bg-slate-50 border border-slate-200 space-y-4">
      <div class="flex items-center justify-between">
        <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500">Form Isi Pulsa All Operator</span>
        <span v-if="ppobConfig.mock_mode" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase">MODE SIMULASI</span>
      </div>

      <div class="space-y-3">
        <label for="pulsa_phone_input" class="block text-xs font-bold text-slate-700">
          Nomor Handphone <span class="text-rose-500">*</span>
        </label>

        <div class="flex flex-col sm:flex-row gap-3">
          <div class="relative flex-1">
            <input
              id="pulsa_phone_input"
              :value="phoneNumber"
              @input="onPhoneInput"
              @keyup.enter.prevent="runInquiry"
              type="tel"
              placeholder="08xxxxxxxxxx"
              class="w-full px-4 py-3 min-h-[44px] rounded-2xl border border-slate-300 text-sm font-semibold tracking-wide bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition outline-none"
            />
            <div v-if="isDetecting" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              <RefreshCw class="w-4 h-4 animate-spin" />
            </div>
          </div>

          <button
            @click="runInquiry"
            :disabled="!isValidPhoneNumber || isChecking"
            type="button"
            class="px-6 py-3 min-h-[44px] rounded-2xl text-xs font-bold transition flex items-center justify-center gap-2 shadow-xs shrink-0 w-full sm:w-auto"
            :class="isValidPhoneNumber && !isChecking
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'"
          >
            <RefreshCw v-if="isChecking" class="w-4 h-4 animate-spin" />
            <Search v-else class="w-4 h-4" />
            <span>{{ isChecking ? 'Mengecek...' : 'Cek Operator' }}</span>
          </button>
        </div>

        <p class="text-[11px] text-slate-500">
          Masukkan nomor handphone untuk mendeteksi operator secara otomatis.
        </p>

        <!-- Detected Operator Badge -->
        <div v-if="detectedOperator" class="p-3 rounded-2xl bg-indigo-50 border border-indigo-200 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span class="text-xs font-bold text-indigo-900">Operator terdeteksi: {{ detectedOperator.display_name || detectedOperator.name }}</span>
          </div>
          <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-200 text-indigo-900 uppercase">
            {{ detectedOperator.code }}
          </span>
        </div>

        <div v-if="detectionError" class="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-center justify-between">
          <span>{{ detectionError }}</span>
          <div class="flex gap-1.5 overflow-x-auto py-1">
            <button
              v-for="op in operators"
              :key="op.id"
              @click="selectOperatorManually(op)"
              type="button"
              class="px-2.5 py-1 rounded-xl bg-white border border-amber-300 text-[10px] font-bold text-amber-950 hover:bg-amber-100 transition"
            >
              {{ op.name }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. Nominal Pulsa SKU Selection Grid -->
    <div v-if="selectedOperatorId" class="space-y-4">
      <div class="flex items-center justify-between">
        <h4 class="font-bold text-xs text-slate-900">Pilih Nominal Pulsa {{ detectedOperator?.name }}</h4>

        <span v-if="!canSelectSku" class="text-[11px] text-amber-700 font-medium">
          Lakukan pengecekan nomor terlebih dahulu
        </span>
        <span v-else class="text-[11px] text-emerald-700 font-bold bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 flex items-center gap-1">
          <CheckCircle2 class="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          <span>Nomor siap digunakan untuk simulasi.</span>
        </span>
      </div>

      <div v-if="availableSkus.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div
          v-for="sku in availableSkus"
          :key="sku.id"
          @click="canSelectSku ? selectedSku = sku : null"
          class="p-4 rounded-2xl border transition flex flex-col justify-between space-y-3 relative"
          :class="[
            !canSelectSku ? 'opacity-60 cursor-not-allowed bg-slate-50 border-slate-200' : 'cursor-pointer',
            selectedSku?.id === sku.id ? 'border-indigo-600 bg-indigo-50/90 ring-2 ring-indigo-500/20' : 'border-slate-200 bg-white hover:border-slate-300'
          ]"
        >
          <div>
            <div class="flex items-center justify-between">
              <span class="font-mono text-[10px] font-bold text-slate-400">{{ sku.code }}</span>
              <span class="text-[9px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">Resmi</span>
            </div>
            <h5 class="font-bold text-xs text-slate-900 mt-1">{{ sku.name }}</h5>
            <p class="text-xs font-black text-indigo-600 mt-2">{{ formatRupiah(sku.fallback_selling_price_minor) }}</p>
          </div>

          <button
            :disabled="!canSelectSku"
            type="button"
            class="w-full py-2 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1"
            :class="selectedSku?.id === sku.id
              ? 'bg-indigo-600 text-white'
              : (canSelectSku ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 cursor-pointer' : 'bg-slate-200 text-slate-400 cursor-not-allowed')"
          >
            <Check v-if="selectedSku?.id === sku.id" class="w-3.5 h-3.5" />
            <span>{{ selectedSku?.id === sku.id ? 'Terpilih' : 'Pilih Nominal' }}</span>
          </button>
        </div>
      </div>
      <p v-else class="text-xs text-slate-500">Tidak ada nominal pulsa aktif untuk operator ini.</p>
    </div>

    <!-- Error Alert -->
    <div v-if="inquiryError" class="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-xs flex items-center gap-2">
      <AlertTriangle class="w-4 h-4 text-rose-600 shrink-0" />
      <span>{{ inquiryError }}</span>
    </div>

    <!-- Action Trigger for Review Modal -->
    <div v-if="canSelectSku && selectedSku" class="p-5 rounded-2xl bg-indigo-900 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-lg">
      <div>
        <span class="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">Nominal Pulsa Terpilih</span>
        <span class="text-lg font-black text-amber-300">{{ selectedSku.name }}</span>
        <p class="text-xs text-indigo-200 mt-0.5">Harga: {{ formatRupiah(selectedSku.fallback_selling_price_minor) }}</p>
      </div>

      <button
        @click="openReviewModal"
        type="button"
        class="w-full sm:w-auto px-6 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs transition shadow-md flex items-center justify-center gap-2 cursor-pointer"
      >
        <span>Lanjutkan Simulasi Pembelian</span>
        <ArrowRight class="w-4 h-4" />
      </button>
    </div>

    <!-- Purchase Review Modal -->
    <div v-if="reviewModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6 shadow-2xl border border-slate-100 relative">
        <div class="flex items-center justify-between border-b border-slate-100 pb-4">
          <div class="flex items-center gap-2">
            <span class="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-extrabold text-[10px] uppercase">
              MODE SIMULASI
            </span>
            <h3 class="font-bold text-sm text-slate-900">Review Pembelian Pulsa</h3>
          </div>
          <button @click="reviewModalOpen = false" class="text-slate-400 hover:text-slate-600">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div class="space-y-3 text-xs">
          <div class="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
            <div class="flex justify-between">
              <span class="text-slate-500">Nomor Tujuan:</span>
              <span class="font-mono font-bold text-slate-900">{{ phoneNumber }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Operator:</span>
              <span class="font-bold text-slate-900">{{ detectedOperator?.name }}</span>
            </div>
            <div class="flex justify-between border-t border-slate-200 pt-2">
              <span class="text-slate-500">Produk Pulsa:</span>
              <span class="font-bold text-indigo-600">{{ selectedSku?.name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Harga Jual:</span>
              <span class="font-black text-slate-900">{{ formatRupiah(selectedSku?.fallback_selling_price_minor) }}</span>
            </div>
          </div>

          <div class="p-3.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-[11px] leading-relaxed flex items-start gap-2.5">
            <Info class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span>Mode simulasi: Transaksi simulasi tidak mengirim pulsa dan tidak memotong saldo.</span>
          </div>
        </div>

        <div class="flex items-center gap-3 pt-2">
          <button
            @click="reviewModalOpen = false"
            type="button"
            class="flex-1 py-3 rounded-2xl border border-slate-300 text-slate-700 font-bold text-xs hover:bg-slate-50 transition"
          >
            Kembali
          </button>
          <button
            @click="confirmPurchase"
            :disabled="isSubmitting"
            type="button"
            class="flex-1 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
          >
            <RefreshCw v-if="isSubmitting" class="w-4 h-4 animate-spin" />
            <span>{{ isSubmitting ? 'Memproses...' : 'Konfirmasi Simulasi' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Purchase Result Modal -->
    <div v-if="resultModalOpen && purchaseResult" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6 shadow-2xl border border-slate-100 relative">
        <div class="text-center space-y-2">
          <div class="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-xs">
            <CheckCircle2 class="w-7 h-7" />
          </div>
          <h3 class="text-lg font-black text-slate-900">SIMULASI BERHASIL</h3>
          <span class="inline-block px-3 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-extrabold uppercase">
            SIMULASI LOKAL
          </span>
        </div>

        <!-- Serial Number Card -->
        <div class="p-5 rounded-2xl bg-slate-900 text-white text-center space-y-2 shadow-md">
          <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">NOMOR SERIAL SIMULASI</span>
          <div class="font-mono text-sm sm:text-base font-black text-amber-300 tracking-wider">
            {{ purchaseResult.serial_number }}
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3 text-xs bg-slate-50 p-4 rounded-2xl border border-slate-200">
          <div>
            <span class="text-[10px] text-slate-500 block">Jenis Produk</span>
            <span class="font-bold text-slate-900">Pulsa</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Operator</span>
            <span class="font-bold text-indigo-600">{{ detectedOperator?.name }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">No. Transaksi</span>
            <span class="font-mono font-bold text-slate-900 truncate block">{{ purchaseResult.transaction_reference }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Nomor Tujuan</span>
            <span class="font-mono font-bold text-slate-900">{{ maskIdentifier(purchaseResult.customer_number) }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">SKU</span>
            <span class="font-bold text-slate-900">{{ selectedSku?.name }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Harga Jual</span>
            <span class="font-bold text-slate-900">{{ formatRupiah(purchaseResult.selling_price_minor) }}</span>
          </div>
        </div>

        <p class="text-[11px] text-slate-500 italic text-center">
          * Transaksi simulasi tidak mengirim pulsa dan tidak memotong saldo.
        </p>

        <button
          @click="resetFlow"
          type="button"
          class="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md transition cursor-pointer"
        >
          Transaksi Baru
        </button>
      </div>
    </div>
  </div>
</template>
